import type { DialogContentProps } from '@radix-ui/react-dialog';
import * as RadixDialog from '@radix-ui/react-dialog';
import { cva } from 'class-variance-authority';
import { forwardRef } from 'react';
import type { DialogSize } from '../dialog.types';

type ContainerProps = {
  isConfirmation: boolean;
  isCentered: boolean;
  onClose: () => void;
  size: DialogSize;
  open: boolean;
  preventEscapeKeyDown?: boolean;
} & DialogContentProps;

const Container = forwardRef<HTMLDivElement, ContainerProps>(
  (
    { children, onEscapeKeyDown, onClose, size, isConfirmation, isCentered, preventEscapeKeyDown, open, ...props },
    ref,
  ) => {
    const handleEscapeKeyDown = (event: KeyboardEvent) => {
      if (preventEscapeKeyDown) {
        event.preventDefault();
      } else if (onEscapeKeyDown) {
        onEscapeKeyDown(event);
      } else {
        onClose();
      }
    };

    return (
      <RadixDialog.Content
        {...props}
        className={containerStyles({ size, isConfirmation, isCentered })}
        onEscapeKeyDown={handleEscapeKeyDown}
        onPointerDownOutside={onClose}
        ref={ref}
      >
        {children}
      </RadixDialog.Content>
    );
  },
);

const containerStyles = cva(
  [
    'fixed flex flex-col left-0 right-0 mx-auto',
    'bg-primary shadow-lg isolate overflow-visible',
    'border border-tertiary border-solid rounded-xl',
    'data-[state=open]:animate-scaleIn',
    'data-[state=closed]:animate-scaleOut',
    'transition-[width] duration-[240ms] ease-[cubic-bezier(0.23,1,0.32,1)] motion-reduce:transition-none',
  ],
  {
    variants: {
      size: {
        // iOS Safari's vh is the viewport with its toolbar hidden, and the toolbar never hides while a
        // dialog locks scrolling, so phones size to dvh or the footer lands under the toolbar.
        'full-screen': 'inset-0 w-[calc(100vw-40px)] h-[calc(100dvh-40px)] top-5 md:h-[calc(100vh-40px)]',
        wide: 'w-[960px] max-w-[calc(100vw-32px)] max-h-[calc(100dvh-48px)] md:max-h-[calc(100vh-48px)]',
        large: 'w-[800px] max-w-[calc(100vw-32px)] max-h-[calc(100dvh-48px)] md:max-h-[calc(100vh-48px)]',
        default: 'w-[650px] max-w-[calc(100vw-32px)] max-h-[calc(100dvh-48px)] md:max-h-[calc(100vh-48px)]',
        compact: 'w-[500px] max-w-[calc(100vw-32px)] max-h-[calc(100dvh-48px)] md:max-h-[calc(100vh-48px)]',
      },
      isConfirmation: {
        true: 'z-confirmation-dialog',
        false: 'z-dialog',
      },
      isCentered: {
        true: 'top-1/2 -translate-y-1/2',
        false: '',
      },
    },
    compoundVariants: [
      {
        isCentered: false,
        size: ['wide', 'large', 'default', 'compact'],
        className: 'top-6',
      },
    ],
    defaultVariants: {
      size: 'default',
      isConfirmation: false,
      isCentered: false,
    },
  },
);

export default Container;
